import { useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useLazyQuery,
} from 'react-apollo';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { expValEquals } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { RelationsForContentQuery } from '../RelationsForContentQuery.experimentalgraphql';
import type { RelationsForContentQuery as RelationsForContentQueryInterface } from '../__types__/RelationsForContentQuery';

import { useExperimentalRelationsForContentQuery } from './useExperimentalRelationsForContentQuery';

const isKnownError = (error: ApolloErrorInterop) => {
	return error.graphQLErrors.some(
		(e) =>
			e.message === 'User does not have permission to view the relationship' ||
			e.message === 'Target entity does not exist',
	);
};

export const useContentRelations = (
	relationName: string,
	contentId: string,
	isUnpublishedDraft: boolean | null,
) => {
	const variables = {
		relationName,
		sourceType: 'user',
		targetKey: contentId,
		targetStatus: 'draft',
	};

	const relationsForContentArgs = {
		variables,
	};

	const [getRelations, { data, loading, error }] = expValEquals(
		'confluence_deprecate_experimental_schema_exp_1',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalRelationsForContentQuery(relationsForContentArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useLazyQuery<RelationsForContentQueryInterface>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				RelationsForContentQuery,
				relationsForContentArgs,
			);

	useEffect(() => {
		// If we're reverting rather than deleting
		if (!isUnpublishedDraft) {
			getRelations();
		}
	}, [isUnpublishedDraft, getRelations]);

	useEffect(() => {
		if (error && isKnownError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	return {
		relations: data?.experimentalRelationsForContent?.results ?? [],
		loading,
		error,
	};
};
